import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdSettings, MdQrCode2, MdAccountBalance, MdUpload, MdDeleteOutline } from 'react-icons/md';
import Button from '../common/Button';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';
import * as feeSvc from '../../services/feeService';

const LABEL_CLS = 'block text-xs font-medium text-muted mb-1.5';
const INPUT_CLS = 'w-full bg-card border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-secondary/30';

// Nepal payment methods (bank account + eSewa/Khalti/Fonepay/Bank QR codes) that students see
// when they pay. Relocated from the Payments page "Fee Settings" tab into Settings → Fees &
// Billing so all fee/billing config lives in one place. Logic is unchanged.
export default function PaymentMethodsSection() {
  const qc                  = useQueryClient();
  const { isReadOnly }      = useImpersonation();
  const v                   = useClubVocab();
  const [form, setForm]     = useState({ bankAccountName: '', bankAccountNumber: '', bankName: '', esewaId: '', khaltiId: '', fonepayId: '', paymentInstructions: '' });
  const [editing,  setEditing]  = useState(false);
  const [uploading, setUploading] = useState(null); // 'esewa'|'khalti'|'fonepay'|'bank'
  const qrRefs = { esewa: useRef(), khalti: useRef(), fonepay: useRef(), bank: useRef() };

  const { data, isPending, refetch } = useQuery({
    queryKey: ['payment-settings'],
    queryFn:  () => feeSvc.getPaymentSettings().then(r => r.data),
  });
  const ps = data?.paymentSettings;

  const saveMutation = useMutation({
    mutationFn: () => feeSvc.updatePaymentSettings(form),
    onSuccess:  () => { toast.success('Payment details saved'); setEditing(false); refetch(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Save failed'),
  });

  const startEdit = () => {
    if (ps) setForm({
      bankAccountName:     ps.bankAccountName     || '',
      bankAccountNumber:   ps.bankAccountNumber   || '',
      bankName:            ps.bankName             || '',
      esewaId:             ps.esewaId             || '',
      khaltiId:            ps.khaltiId            || '',
      fonepayId:           ps.fonepayId           || '',
      paymentInstructions: ps.paymentInstructions || '',
    });
    setEditing(true);
  };

  const handleQrUpload = async (method, file) => {
    if (!file) return;
    setUploading(method);
    try {
      const fd = new FormData();
      fd.append('qrImage', file);
      await feeSvc.uploadQrImage(method, fd);
      toast.success(`${method.charAt(0).toUpperCase() + method.slice(1)} QR uploaded`);
      qc.invalidateQueries({ queryKey: ['payment-settings'] });
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleQrRemove = async (method) => {
    try {
      await feeSvc.removeQrImage(method);
      toast.success(`${method.charAt(0).toUpperCase() + method.slice(1)} QR removed`);
      qc.invalidateQueries({ queryKey: ['payment-settings'] });
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Remove failed');
    }
  };

  if (isPending) return <div className="h-24 bg-nav-hover animate-pulse rounded-2xl mt-6" />;

  const QR_METHODS = [
    { key: 'esewa',   label: 'eSewa',   idField: 'esewaId',   idLabel: 'eSewa ID / Phone',   color: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700' },
    { key: 'khalti',  label: 'Khalti',  idField: 'khaltiId',  idLabel: 'Khalti ID / Phone',  color: 'bg-purple-50 border-purple-200', badge: 'bg-purple-100 text-purple-700' },
    { key: 'fonepay', label: 'Fonepay', idField: 'fonepayId', idLabel: 'Fonepay ID / Phone', color: 'bg-blue-50 border-blue-200',   badge: 'bg-blue-100 text-blue-700' },
    { key: 'bank',    label: 'Bank QR', idField: null,        idLabel: null,                  color: 'bg-nav-hover border-divider',   badge: 'bg-nav-hover text-muted' },
  ];

  const hasAnyMethod = !!(
    ps?.esewaId || ps?.khaltiId || ps?.fonepayId || ps?.bankAccountNumber ||
    ps?.esewaQr?.url || ps?.khaltiQr?.url || ps?.fonepayQr?.url || ps?.bankQr?.url
  );

  return (
    <div>
      {!editing && !hasAnyMethod && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4 text-xs text-amber-700">
          <MdQrCode2 className="text-lg flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-0.5">No payment method configured yet</p>
            <p className="text-amber-600">{v.members} won't see how to pay until you add at least one option — eSewa, Khalti, Fonepay, or bank account. Click <strong>Edit Details</strong> below to get started.</p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-secondary">Payment Details {v.members} See</h3>
          <p className="text-xs text-subtle mt-0.5">Bank account and QR codes shown when {v.members.toLowerCase()} pay their fee.</p>
        </div>
        {!editing && (
          <Button type="button" variant="ghost" onClick={startEdit}><MdSettings /> Edit Details</Button>
        )}
      </div>

      {/* Bank account details */}
      <div className="bg-nav-hover rounded-2xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <MdAccountBalance className="text-muted" />
          <p className="text-sm font-semibold text-secondary">Bank Account</p>
        </div>
        {editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={LABEL_CLS}>Bank Name</label>
              <input value={form.bankName} onChange={e => setForm(f => ({ ...f, bankName: e.target.value }))}
                placeholder="e.g. Nepal Investment Bank" className={INPUT_CLS} />
            </div>
            <div>
              <label className={LABEL_CLS}>Account Holder Name</label>
              <input value={form.bankAccountName} onChange={e => setForm(f => ({ ...f, bankAccountName: e.target.value }))}
                placeholder="e.g. Taekwondo Club" className={INPUT_CLS} />
            </div>
            <div>
              <label className={LABEL_CLS}>Account Number</label>
              <input value={form.bankAccountNumber} onChange={e => setForm(f => ({ ...f, bankAccountNumber: e.target.value }))}
                placeholder="e.g. 01234567890123" className={INPUT_CLS} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {[
              ['Bank', ps?.bankName || '—'],
              ['Account Name', ps?.bankAccountName || '—'],
              ['Account Number', ps?.bankAccountNumber || '—'],
            ].map(([lbl, val]) => (
              <div key={lbl}>
                <p className="text-xs text-subtle">{lbl}</p>
                <p className="font-medium text-secondary">{val}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* eSewa / Khalti / Fonepay ID fields */}
      {editing && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {QR_METHODS.filter(m => m.idField).map(m => (
            <div key={m.key}>
              <label className={LABEL_CLS}>{m.idLabel}</label>
              <input
                value={form[m.idField]}
                onChange={e => setForm(f => ({ ...f, [m.idField]: e.target.value }))}
                placeholder={`e.g. 98XXXXXXXX`}
                className={INPUT_CLS}
              />
            </div>
          ))}
        </div>
      )}

      {/* Payment instructions */}
      {editing ? (
        <div className="mb-4">
          <label className={LABEL_CLS}>Payment Instructions (shown to {v.members.toLowerCase()})</label>
          <textarea
            value={form.paymentInstructions}
            onChange={e => setForm(f => ({ ...f, paymentInstructions: e.target.value }))}
            placeholder="e.g. After paying via eSewa or Khalti, take a screenshot and upload it below."
            className={`${INPUT_CLS} resize-none`}
            rows={2}
          />
        </div>
      ) : ps?.paymentInstructions ? (
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 text-xs text-blue-700">
          {ps.paymentInstructions}
        </div>
      ) : null}

      {/* Save / Cancel */}
      {editing && (
        <div className="flex gap-2 mb-6">
          <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}>
            {saveMutation.isPending ? 'Saving…' : 'Save Payment Details'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      )}

      {/* QR images grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {QR_METHODS.map(m => {
          const qrUrl = ps?.[`${m.key}Qr`]?.url;
          const isUp  = uploading === m.key;
          const id    = ps?.[m.idField] || '';
          return (
            <div key={m.key} className={`rounded-2xl border p-3 ${m.color}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${m.badge}`}>{m.label}</span>
                <MdQrCode2 className="text-subtle text-lg" />
              </div>
              {m.idField && !editing && id && (
                <p className="text-xs text-muted font-mono mb-2">{id}</p>
              )}
              {qrUrl ? (
                <div className="space-y-1.5">
                  <img src={qrUrl} alt={`${m.label} QR`} className="w-full aspect-square object-contain rounded-xl bg-card border border-divider" />
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => qrRefs[m.key].current?.click()}
                      disabled={isUp || isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="flex-1 text-xs py-1.5 rounded-lg bg-card border border-divider text-muted hover:bg-nav-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-medium"
                    >
                      {isUp ? '…' : 'Replace'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleQrRemove(m.key)}
                      disabled={isReadOnly}
                      className="px-2 py-1.5 rounded-lg bg-card border border-divider text-red-400 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isReadOnly ? 'Disabled: read-only support mode' : 'Remove QR'}
                    >
                      <MdDeleteOutline className="text-sm" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => qrRefs[m.key].current?.click()}
                  disabled={isUp || isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="w-full flex flex-col items-center justify-center py-4 border-2 border-dashed border-divider rounded-xl text-xs text-subtle hover:border-secondary hover:text-muted transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isUp ? (
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <MdUpload className="text-xl mb-1" />
                      Upload QR
                    </>
                  )}
                </button>
              )}
              <input
                ref={qrRefs[m.key]}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => { if (e.target.files?.[0]) handleQrUpload(m.key, e.target.files[0]); e.target.value = ''; }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
